import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button, HelperText, Label, Spinner, TextInput } from "flowbite-react";
import { useAuthentication, useTranslation, useToast } from "@repo/core";
import { useRequestPasswordReset } from "@repo/api";
import {
  extractPasswordResetError,
  isUnknownEmailError,
} from "../utils/authErrors";

/**
 * "Forgot your password?" step one: ask the backend to mail a reset link.
 * The reset link itself is handled by the platform (ANP) frontend, so this
 * page ends at the confirmation screen rather than collecting a new password.
 */
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>();
  const [sent, setSent] = useState(false);

  const { isAuthenticated } = useAuthentication();
  const { addErrorMessage } = useToast();
  const { t } = useTranslation();
  const requestPasswordReset = useRequestPasswordReset();

  // Nothing to reset while signed in - the profile page handles that case.
  if (isAuthenticated) {
    return <Navigate to="/profile" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(undefined);

    try {
      await requestPasswordReset.mutateAsync({ email: email.trim() });
      setSent(true);
    } catch (error) {
      console.error("Password reset request failed:", error);

      // 401 is the backend's way of saying "no account under that address".
      // It reads as a field problem, so it goes next to the input rather
      // than into a toast the user has to connect back to the form.
      if (isUnknownEmailError(error)) {
        setEmailError(t("forgotPassword.unknownEmail"));
        return;
      }

      const message = extractPasswordResetError(error);
      if (message) {
        setEmailError(message);
      } else {
        addErrorMessage(t("forgotPassword.genericError"));
      }
    }
  };

  if (sent) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow dark:bg-gray-800">
          <h2 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
            {t("forgotPassword.successTitle")}
          </h2>
          <p className="mb-2 text-gray-600 dark:text-gray-400">
            {t("forgotPassword.successBody1")}
          </p>
          <p className="font-medium text-gray-900 dark:text-white">
            {t("forgotPassword.successBody2")}
          </p>
          <Link
            to="/login"
            className="mt-6 inline-block text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            {t("forgotPassword.backToLogin")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow dark:bg-gray-800">
        <h2 className="mb-2 text-center text-2xl font-bold text-gray-900 dark:text-white">
          {t("forgotPassword.title")}
        </h2>
        <p className="mb-6 text-center text-sm text-gray-600 dark:text-gray-400">
          {t("forgotPassword.description")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <Label htmlFor="email">{t("forgotPassword.emailLabel")}</Label>
            <TextInput
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder={t("forgotPassword.emailPlaceholder")}
              value={email}
              color={emailError ? "failure" : undefined}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(undefined);
              }}
            />
            {emailError && <HelperText color="failure">{emailError}</HelperText>}
          </div>

          <Button
            type="submit"
            color="red"
            className="w-full"
            disabled={requestPasswordReset.isPending || email.trim().length === 0}
          >
            {requestPasswordReset.isPending && <Spinner size="sm" className="mr-2" light />}
            {t("forgotPassword.submitButton")}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          {t("forgotPassword.noAccount")}{" "}
          <Link to="/signup" className="font-medium text-blue-600 hover:underline dark:text-blue-400">
            {t("forgotPassword.createAccount")}
          </Link>
        </p>
        <p className="mt-2 text-center text-sm">
          <Link to="/login" className="font-medium text-blue-600 hover:underline dark:text-blue-400">
            {t("forgotPassword.backToLogin")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
